/* ETC Redesign — vanilla port of motion.jsx animations.
   Mounts on DOMContentLoaded. No framework dependencies. */
(function () {
  "use strict";

  // ───────────────────────────────────────────────
  // Reveal — IntersectionObserver for [data-reveal]
  // ───────────────────────────────────────────────
  function mountReveals(root) {
    var nodes = (root || document).querySelectorAll("[data-reveal]:not(.is-in)");
    if (!nodes.length) return;

    var obs = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add("is-in");
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.05, rootMargin: "0px 0px -40px 0px" });

    nodes.forEach(function (el) {
      var kind = el.dataset.reveal || "up";
      var delay = el.dataset.revealDelay;
      var stagger = el.dataset.revealStagger;
      el.classList.add("reveal", "reveal-" + kind);
      if (delay) el.style.setProperty("--reveal-delay", delay + "ms");
      if (stagger) el.style.setProperty("--reveal-stagger", stagger + "ms");

      // Above-the-fold: reveal on next paint without waiting for IO
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          var r = el.getBoundingClientRect();
          if (r.top < window.innerHeight * 1.1 && r.bottom > -50) {
            el.classList.add("is-in");
            obs.unobserve(el);
          } else {
            obs.observe(el);
          }
        });
      });
    });
  }

  // ───────────────────────────────────────────────
  // SplitText — wraps each word/char in spans
  // <span data-split-text data-split-by="word|char" data-split-stagger="40">Hello world</span>
  // ───────────────────────────────────────────────
  function mountSplits(root) {
    var nodes = (root || document).querySelectorAll("[data-split-text]:not(.split-text-done)");
    nodes.forEach(function (el) {
      var text = el.textContent;
      var by = el.dataset.splitBy || "word";
      var stagger = parseInt(el.dataset.splitStagger || "30", 10);
      var delay = parseInt(el.dataset.splitDelay || "0", 10);
      var kind = el.dataset.splitKind || "up";
      var parts = by === "char" ? text.split("") : text.split(" ");
      el.setAttribute("aria-label", text);
      el.classList.add("split-text", "split-text-done", "reveal", "split-" + kind);
      var html = "";
      parts.forEach(function (p, i) {
        var ch = p === " " ? " " : p;
        html += '<span class="split-part" style="--i:' + i + ';--stagger:' + stagger + 'ms;--delay:' + delay + 'ms"><span class="split-inner">' + escapeHtml(ch) + '</span></span>';
        if (by === "word" && i < parts.length - 1) html += " ";
      });
      el.innerHTML = html;
    });
    // After splitting, treat each split as a reveal target
    mountReveals(root);
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#39;" }[c];
    });
  }

  // ───────────────────────────────────────────────
  // Magnetic — [data-magnetic] slides inner toward cursor
  // ───────────────────────────────────────────────
  function mountMagnetic(root) {
    if (matchMedia("(hover: none)").matches) return;
    var nodes = (root || document).querySelectorAll("[data-magnetic]:not(.magnetic-done)");
    nodes.forEach(function (el) {
      el.classList.add("magnetic", "magnetic-done");
      var inner = el.querySelector(".magnetic-inner");
      if (!inner) {
        inner = document.createElement("span");
        inner.className = "magnetic-inner";
        while (el.firstChild) inner.appendChild(el.firstChild);
        el.appendChild(inner);
      }
      var strength = parseFloat(el.dataset.magnetic) || 0.25;
      el.addEventListener("mousemove", function (e) {
        var r = el.getBoundingClientRect();
        var x = (e.clientX - r.left - r.width / 2) * strength;
        var y = (e.clientY - r.top - r.height / 2) * strength;
        inner.style.transform = "translate(" + x + "px," + y + "px)";
      });
      el.addEventListener("mouseleave", function () {
        inner.style.transform = "";
      });
    });
  }

  // ───────────────────────────────────────────────
  // ETC cursor — "lens" con crop marks + etiqueta editorial contextual
  // ───────────────────────────────────────────────
  function mountCursor() {
    if (matchMedia("(hover: none)").matches) return;
    if (document.querySelector(".etc-cur")) return;
    var ring = document.createElement("div");
    ring.className = "etc-cur";
    var label = document.createElement("span");
    label.className = "etc-cur-label";
    ring.appendChild(label);
    var dot = document.createElement("div");
    dot.className = "etc-cur-dot";
    document.body.appendChild(ring);
    document.body.appendChild(dot);

    var mx = window.innerWidth / 2, my = window.innerHeight / 2;
    var rx = mx, ry = my, down = false;
    var tick = function () {
      rx += (mx - rx) * 0.2;
      ry += (my - ry) * 0.2;
      dot.style.transform = "translate3d(" + mx + "px," + my + "px,0)";
      ring.style.transform = "translate3d(" + rx + "px," + ry + "px,0) scale(" + (down ? 0.8 : 1) + ")";
      requestAnimationFrame(tick);
    };
    window.addEventListener("mousemove", function (e) { mx = e.clientX; my = e.clientY; }, { passive: true });
    window.addEventListener("mousedown", function () { down = true; });
    window.addEventListener("mouseup", function () { down = false; });
    document.addEventListener("mouseleave", function () { ring.style.opacity = 0; dot.style.opacity = 0; });
    document.addEventListener("mouseenter", function () { ring.style.opacity = ""; dot.style.opacity = ""; });
    tick();

    function labelFor(el) {
      if (el.dataset && el.dataset.cursorLabel) return el.dataset.cursorLabel;
      if (el.closest(".lookbook-hotspot")) return "+";
      if (el.closest(".prod-card, .cat-card, .ig-cell, .split-card, .lookbook-image")) return "VER";
      return "→";
    }
    var interactive = "a, button, [data-cursor='link'], [data-cursor-label], .prod-card, .cat-card, .ig-cell, .split-card, .lookbook-hotspot, .lookbook-image";
    document.addEventListener("mouseover", function (e) {
      var t = e.target.closest && e.target.closest(interactive);
      if (t) { label.textContent = labelFor(t); ring.classList.add("is-hover"); dot.style.opacity = 0; }
    });
    document.addEventListener("mouseout", function (e) {
      var t = e.target.closest && e.target.closest(interactive);
      var into = e.relatedTarget && e.relatedTarget.closest && e.relatedTarget.closest(interactive);
      if (t && !into) { ring.classList.remove("is-hover"); dot.style.opacity = ""; }
    });
  }

  // ───────────────────────────────────────────────
  // Scroll progress bar — top fixed bar
  // ───────────────────────────────────────────────
  function mountScrollProgress() {
    if (document.querySelector(".scroll-progress")) return;
    var wrap = document.createElement("div");
    wrap.className = "scroll-progress";
    var bar = document.createElement("div");
    bar.className = "scroll-progress-bar";
    wrap.appendChild(bar);
    document.body.appendChild(wrap);

    var raf = null;
    var update = function () {
      raf = null;
      var h = document.documentElement.scrollHeight - window.innerHeight;
      var pct = h > 0 ? (window.scrollY / h) * 100 : 0;
      bar.style.transform = "scaleX(" + (pct / 100) + ")";
    };
    window.addEventListener("scroll", function () {
      if (!raf) raf = requestAnimationFrame(update);
    }, { passive: true });
    update();
  }

  // ───────────────────────────────────────────────
  // Intro — 3 phones with real catalog
  // Mount point: <div data-etc-intro></div> + <script id="etc-intro-data" type="application/json">
  // ───────────────────────────────────────────────
  function mountIntro() {
    var mount = document.querySelector("[data-etc-intro]");
    if (!mount) return;
    if (sessionStorage.getItem("etc-intro-seen") === "1") {
      mount.remove();
      return;
    }
    var dataEl = document.getElementById("etc-intro-data");
    if (!dataEl) { mount.remove(); return; }
    var products;
    try { products = JSON.parse(dataEl.textContent); }
    catch (e) { mount.remove(); return; }
    if (!Array.isArray(products) || products.length === 0) { mount.remove(); return; }

    var stripA = products.slice(0, 10);
    var stripB = products.slice(10, 20).length ? products.slice(10, 20) : stripA;
    var stripC = products.slice(20, 30).concat(products.slice(0, 4));
    if (stripC.length === 0) stripC = stripA;
    var strips = [stripA, stripB, stripC];

    var stageHtml = strips.map(function (strip, idx) {
      var tiles = strip.concat(strip).concat(strip).map(function (p) {
        var priceK = Math.round((p.price || 0) / 100000); // Shopify prices are in cents
        return '<div class="phone-tile"><img src="' + escapeAttr(p.img || "") + '" alt="" loading="eager" decoding="async"/>' +
               '<div class="phone-tile-label"><span>' + escapeHtml(p.name || "") + '</span><em>$' + priceK + 'k</em></div></div>';
      }).join("");
      return '<div class="phone phone-' + (idx + 1) + '"><div class="phone-bezel"><div class="phone-notch"></div>' +
             '<div class="phone-screen"><div class="phone-feed">' + tiles + '</div></div>' +
             '<div class="phone-glare"></div></div></div>';
    }).join("");

    var logoUrl = mount.dataset.logoUrl || "";
    mount.className = "intro2 intro2-enter";
    mount.innerHTML =
      '<div class="intro2-grid"></div>' +
      '<div class="intro2-corner intro2-corner-tl"><div class="intro2-eyebrow">ETC <span>·</span> 2026</div>' +
      '<div class="intro2-eyebrow-thin">Tienda femenina <span>·</span> Bucaramanga, CO</div></div>' +
      '<div class="intro2-corner intro2-corner-tr"><div class="intro2-count">' +
      '<span class="intro2-count-num" data-count>000</span><span class="intro2-count-sep">/</span>' +
      '<span class="intro2-count-tot">100</span></div>' +
      '<div class="intro2-eyebrow-thin">Cargando colección</div></div>' +
      '<div class="intro2-stage">' + stageHtml + '</div>' +
      '<div class="intro2-logo-wrap"><img src="' + escapeAttr(logoUrl) + '" alt="ETC" class="intro2-logo"/></div>' +
      '<div class="intro2-corner intro2-corner-bl"><div class="intro2-marquee"><div class="intro2-marquee-track">' +
      (function () {
        var marquee = "";
        for (var i = 0; i < 6; i++) {
          marquee += '<span>Vestidos <em>·</em> Tops <em>·</em> Conjuntos <em>·</em> ' + products.length + ' piezas <em>·</em>&nbsp;</span>';
        }
        return marquee;
      })() +
      '</div></div></div>' +
      '<div class="intro2-corner intro2-corner-br"><div class="intro2-eyebrow-thin">' +
      'Pago contraentrega <span>·</span> ADDI <span>·</span> Envío gratis $250k+</div></div>' +
      '<div class="intro2-flash"></div>';

    var countEl = mount.querySelector("[data-count]");
    var start = performance.now();
    var dur = 2800;
    var tick = function (t) {
      var e = Math.min(1, (t - start) / dur);
      var eased = e < 0.5 ? 4 * e * e * e : 1 - Math.pow(-2 * e + 2, 3) / 2;
      countEl.textContent = String(Math.round(eased * 100)).padStart(3, "0");
      if (e < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);

    setTimeout(function () { mount.classList.remove("intro2-enter"); mount.classList.add("intro2-scrolling"); }, 500);
    setTimeout(function () { mount.classList.remove("intro2-scrolling"); mount.classList.add("intro2-snap"); }, 2900);
    setTimeout(function () { mount.classList.remove("intro2-snap"); mount.classList.add("intro2-burst"); }, 3500);
    setTimeout(function () {
      mount.remove();
      sessionStorage.setItem("etc-intro-seen", "1");
    }, 3950);
  }

  function escapeAttr(s) {
    return String(s).replace(/"/g, "&quot;").replace(/</g, "&lt;");
  }

  // ───────────────────────────────────────────────
  // Hero video fallback — swap <video> for <img> if it errors
  // ───────────────────────────────────────────────
  function mountVideoFallback() {
    var videos = document.querySelectorAll("video[data-fallback]");
    videos.forEach(function (v) {
      v.addEventListener("error", function () {
        var img = document.createElement("img");
        img.src = v.dataset.fallback;
        img.alt = "Editorial ETC";
        img.style.cssText = "width:100%;height:100%;object-fit:cover;display:block";
        v.replaceWith(img);
      });
    });
  }

  // ───────────────────────────────────────────────
  // Nav scroll-shadow trigger (Nav adds .is-scrolled when scrollY > 30)
  // ───────────────────────────────────────────────
  function mountNavScroll() {
    var nav = document.querySelector("[data-etc-nav]");
    if (!nav) return;
    var onScroll = function () {
      if (window.scrollY > 30) nav.classList.add("is-scrolled");
      else nav.classList.remove("is-scrolled");
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
  }

  // ───────────────────────────────────────────────
  // Boot
  // ───────────────────────────────────────────────
  function boot() {
    mountIntro();
    mountReveals();
    mountSplits();
    mountMagnetic();
    mountCursor();
    mountScrollProgress();
    mountNavScroll();
    mountVideoFallback();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  // Expose for theme editor: when sections are re-rendered, remount
  window.ETCRedesign = {
    remount: function (root) {
      mountReveals(root);
      mountSplits(root);
      mountMagnetic(root);
    }
  };
  document.addEventListener("shopify:section:load", function (e) {
    window.ETCRedesign.remount(e.target);
  });
})();
